/**
 * Dev mode seed data.
 * Local-only — no Supabase calls. Used when `useApp.dev === true`.
 * 4 family members, 12 sample tasks across all states, 5 messages, 3 resources.
 */
import type { DbTask, DbMessage, DbResource, DbMember } from './db'

export const SEED_MEMBERS: DbMember[] = [
  {
    id: 'dev-mom',
    name: 'Sarah',
    avatar_url: null,
    role: 'adult',
    subtype: 'primary',
    ep_opt_out: false,
    created_at: '2026-06-01T00:00:00Z',
    deleted_at: null,
  },
  {
    id: 'dev-dad',
    name: 'James',
    avatar_url: null,
    role: 'adult',
    subtype: 'co_manager',
    ep_opt_out: false,
    created_at: '2026-06-01T00:00:00Z',
    deleted_at: null,
  },
  {
    id: 'dev-emma',
    name: 'Emma',
    avatar_url: null,
    role: 'child',
    subtype: null,
    ep_opt_out: false,
    created_at: '2026-06-01T00:00:00Z',
    deleted_at: null,
  },
  {
    id: 'dev-noah',
    name: 'Noah',
    avatar_url: null,
    role: 'child',
    subtype: null,
    ep_opt_out: false,
    created_at: '2026-06-01T00:00:00Z',
    deleted_at: null,
  },
]

const today = new Date()
const todayStr = today.toISOString()
const dueToday = (offsetDays: number) => {
  const d = new Date(today)
  d.setDate(d.getDate() + offsetDays)
  return d.toISOString()
}

export const SEED_TASKS: DbTask[] = [
  {
    id: 'dev-task-1',
    title: 'Take out the bins',
    type: 'chore',
    priority: 'med',
    ep: 10,
    due: dueToday(0),
    assignees: ['dev-emma'],
    status: 'todo',
    position: 0,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-2',
    title: 'Help Dad with the garden',
    type: 'chore',
    priority: 'low',
    ep: 25,
    due: dueToday(1),
    assignees: ['dev-noah'],
    status: 'todo',
    position: 1,
    created_by: 'dev-dad',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-3',
    title: 'Read for 20 minutes',
    type: 'habit',
    priority: 'low',
    ep: 15,
    due: dueToday(0),
    assignees: ['dev-emma', 'dev-noah'],
    status: 'in_progress',
    position: 2,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-4',
    title: 'Set the table for dinner',
    type: 'chore',
    priority: 'high',
    ep: 5,
    due: dueToday(0),
    assignees: ['dev-noah'],
    status: 'in_progress',
    position: 3,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-5',
    title: 'Tidy the front room',
    type: 'chore',
    priority: 'med',
    ep: 20,
    due: dueToday(0),
    assignees: ['dev-emma'],
    status: 'pending_approval',
    position: 4,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-6',
    title: 'Walk the dog',
    type: 'chore',
    priority: 'med',
    ep: 10,
    due: dueToday(-1),
    assignees: ['dev-noah'],
    status: 'done',
    position: 5,
    created_by: 'dev-dad',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: dueToday(-1),
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-7',
    title: 'Practise piano',
    type: 'habit',
    priority: 'low',
    ep: 15,
    due: dueToday(-2),
    assignees: ['dev-emma'],
    status: 'done',
    position: 6,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: dueToday(-2),
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-8',
    title: 'Pack school bag',
    type: 'chore',
    priority: 'high',
    ep: 5,
    due: dueToday(0),
    assignees: ['dev-emma'],
    status: 'todo',
    position: 7,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-9',
    title: 'Water the plants',
    type: 'chore',
    priority: 'low',
    ep: 10,
    due: dueToday(2),
    assignees: ['dev-noah'],
    status: 'todo',
    position: 8,
    created_by: 'dev-dad',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-10',
    title: 'Tidy bedroom',
    type: 'chore',
    priority: 'med',
    ep: 20,
    due: dueToday(0),
    assignees: ['dev-emma'],
    status: 'in_progress',
    position: 9,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-11',
    title: 'Help with shopping',
    type: 'errand',
    priority: 'med',
    ep: 30,
    due: dueToday(1),
    assignees: ['dev-emma', 'dev-noah'],
    status: 'pending_approval',
    position: 10,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: null,
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
  {
    id: 'dev-task-12',
    title: 'Empty the dishwasher',
    type: 'chore',
    priority: 'low',
    ep: 5,
    due: dueToday(-3),
    assignees: ['dev-noah'],
    status: 'done',
    position: 11,
    created_by: 'dev-dad',
    created_at: todayStr,
    updated_at: todayStr,
    done_at: dueToday(-3),
    archived_at: null,
    recurrence: 'none',
    recurrence_parent_id: null,
  },
]

export const SEED_MESSAGES: DbMessage[] = [
  {
    id: 'dev-msg-1',
    channel: 'family',
    dm_with: null,
    sender: 'dev-mom',
    body: 'Morning everyone — who wants pancakes?',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
  },
  {
    id: 'dev-msg-2',
    channel: 'family',
    dm_with: null,
    sender: 'dev-emma',
    body: 'Me please! With maple syrup!',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 8 + 60_000).toISOString(),
  },
  {
    id: 'dev-msg-3',
    channel: 'family',
    dm_with: null,
    sender: 'dev-noah',
    body: 'Me too. And can I have extra fruit?',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 8 + 120_000).toISOString(),
  },
  {
    id: 'dev-msg-4',
    channel: 'family',
    dm_with: null,
    sender: 'dev-dad',
    body: 'Coming up. I\u2019ll set the table.',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 8 + 180_000).toISOString(),
  },
  {
    id: 'dev-msg-5',
    channel: 'family',
    dm_with: null,
    sender: 'dev-mom',
    body: 'Loved the tidy front room, Emma. The cushions are perfect.',
    created_at: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
  },
]

export const SEED_RESOURCES: DbResource[] = [
  {
    id: 'dev-res-1',
    title: 'Family meal planner',
    description: 'A simple week-of-meals template we use on Sundays.',
    type: 'tool',
    subjects: ['kitchen', 'planning'],
    tags: ['meals', 'weekly'],
    file_url: null,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
  },
  {
    id: 'dev-res-2',
    title: 'Local library storytime times',
    description: 'Saturdays at 10am, free for under-5s.',
    type: 'link',
    subjects: ['emma', 'noah'],
    tags: ['library', 'weekend'],
    file_url: null,
    created_by: 'dev-dad',
    created_at: todayStr,
    updated_at: todayStr,
  },
  {
    id: 'dev-res-3',
    title: 'How to praise effort, not outcome',
    description: 'A short article on growth-mindset language for kids.',
    type: 'article',
    subjects: [],
    tags: ['parenting', 'mindset'],
    file_url: null,
    created_by: 'dev-mom',
    created_at: todayStr,
    updated_at: todayStr,
  },
]

export const SEED_GAMIFICATION: Record<string, { ep: number; pending_ep: number; lifetime_ep: number; level: number; streak_days: number; last_streak_date: string | null; achievements: string[] }> = {
  'dev-emma': { ep: 320, pending_ep: 25, lifetime_ep: 345, level: 4, streak_days: 5, last_streak_date: todayStr.split('T')[0], achievements: ['first-step', 'week-of-fruit'] },
  'dev-noah': { ep: 145, pending_ep: 0, lifetime_ep: 145, level: 2, streak_days: 3, last_streak_date: todayStr.split('T')[0], achievements: ['first-step'] },
  'dev-mom': { ep: 0, pending_ep: 0, lifetime_ep: 0, level: 1, streak_days: 0, last_streak_date: null, achievements: [] },
  'dev-dad': { ep: 0, pending_ep: 0, lifetime_ep: 0, level: 1, streak_days: 0, last_streak_date: null, achievements: [] },
}
